// src/Components/InterviewFeedbackForm.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config';
import './InterviewFeedbackForm.css';

const InterviewFeedbackForm = ({ interviewId, applicationId, onClose, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    overallExperience: 0,
    interviewerProfessionalism: 0,
    questionsRelevance: 0,
    technicalDifficulty: 0,
    platformExperience: 0,
    wouldRecommend: '',
    comments: '',
    improvements: '',
    likedMost: ''
  });

  const [hoveredRatings, setHoveredRatings] = useState({
    overallExperience: 0,
    interviewerProfessionalism: 0,
    questionsRelevance: 0,
    technicalDifficulty: 0,
    platformExperience: 0
  });

  // Debug: Log when component mounts and props change
  useEffect(() => {
    console.log('InterviewFeedbackForm received props:', { interviewId, applicationId });
  }, [interviewId, applicationId]);

  const ratingLabels = {
    1: 'Poor',
    2: 'Fair',
    3: 'Good',
    4: 'Very Good',
    5: 'Excellent'
  };

  const handleRatingChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTextChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateForm = () => {
    // Check if IDs are present
    if (!interviewId || !applicationId) {
      toast.error('Missing interview information. Please try again.');
      console.error('Missing IDs:', { interviewId, applicationId });
      return false;
    }

    const requiredRatings = ['overallExperience', 'interviewerProfessionalism', 'questionsRelevance', 'technicalDifficulty', 'platformExperience'];
    for (const rating of requiredRatings) {
      if (formData[rating] === 0) {
        toast.error(`Please rate ${rating.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    if (!formData.wouldRecommend) {
      toast.error('Please select whether you would recommend us');
      return false;
    }
    return true;
  };

  // ✅ FIXED handleSubmit function - removed undefined variables
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submitted, validating...');
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const payload = {
        interviewId: interviewId,
        applicationId: applicationId,
        feedback: {
          overallExperience: Number(formData.overallExperience),
          interviewerProfessionalism: Number(formData.interviewerProfessionalism),
          questionsRelevance: Number(formData.questionsRelevance),
          technicalDifficulty: Number(formData.technicalDifficulty),
          platformExperience: Number(formData.platformExperience),
          wouldRecommend: formData.wouldRecommend,
          comments: formData.comments || '',
          improvements: formData.improvements || '',
          likedMost: formData.likedMost || ''
        }
      };
      
      console.log('📤 Submitting feedback payload:', payload);
      
      const response = await axios.post(`${API_BASE_URL}/api/applications/interview-feedback`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Feedback response:', response.data);
      
      if (response.data.success) {
        toast.success('Thank you for your valuable feedback!');
        setSubmitted(true);
        setTimeout(() => {
          if (onComplete) onComplete();
          if (onClose) onClose();
        }, 2000);
      } else {
        toast.error(response.data.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('❌ Error submitting feedback:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 400) {
        toast.error(error.response.data.message || 'Invalid data. Please check all fields.');
      } else if (error.response?.status === 404) {
        toast.error('Interview not found. Please try again.');
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to submit feedback. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const RatingStars = ({ field, label, value, hoverValue }) => {
    return (
      <div className="rating-group">
        <label className="rating-label">{label}</label>
        <div className="stars-container">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className={`star-btn ${star <= (hoverValue || value) ? 'active' : ''}`}
              onClick={() => handleRatingChange(field, star)}
              onMouseEnter={() => setHoveredRatings(prev => ({ ...prev, [field]: star }))}
              onMouseLeave={() => setHoveredRatings(prev => ({ ...prev, [field]: 0 }))}
            >
              ★
            </button>
          ))}
          {(hoverValue || value) > 0 && (
            <span className="rating-text">{ratingLabels[hoverValue || value]}</span>
          )}
        </div>
      </div>
    );
  };

  if (submitted) {
    return (
      <div className="feedback-thankyou">
        <div className="thankyou-icon">🎉</div>
        <h2>Thank You!</h2>
        <p>Your feedback has been submitted successfully.</p>
        <p className="thankyou-message">We appreciate your time and valuable input!</p>
      </div>
    );
  }

  return (
    <div className="feedback-modal-overlay">
      <div className="feedback-modal">
        <div className="feedback-header">
          <h2>Share Your Interview Experience</h2>
          <p>Your feedback helps us improve our interview process</p>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="feedback-form">
          <RatingStars 
            field="overallExperience" 
            label="Overall Experience" 
            value={formData.overallExperience}
            hoverValue={hoveredRatings.overallExperience}
          />
          
          <RatingStars 
            field="interviewerProfessionalism" 
            label="Interviewer Professionalism" 
            value={formData.interviewerProfessionalism}
            hoverValue={hoveredRatings.interviewerProfessionalism}
          />
          
          <RatingStars 
            field="questionsRelevance" 
            label="Relevance of Questions" 
            value={formData.questionsRelevance}
            hoverValue={hoveredRatings.questionsRelevance}
          />
          
          <RatingStars 
            field="technicalDifficulty" 
            label="Technical Difficulty Level" 
            value={formData.technicalDifficulty}
            hoverValue={hoveredRatings.technicalDifficulty}
          />
          
          <RatingStars 
            field="platformExperience" 
            label="Platform Experience" 
            value={formData.platformExperience}
            hoverValue={hoveredRatings.platformExperience}
          />
          
          <div className="form-group">
            <label>Would you recommend our company to others?</label>
            <div className="recommend-buttons">
              {['Yes', 'No', 'Maybe'].map(option => (
                <button
                  key={option}
                  type="button"
                  className={`recommend-btn ${formData.wouldRecommend === option ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, wouldRecommend: option }))}
                >
                  {option === 'Yes' && '👍 Yes'}
                  {option === 'No' && '👎 No'}
                  {option === 'Maybe' && '🤔 Maybe'}
                </button>
              ))}
            </div>
          </div>
          
          <div className="form-group">
            <label>What did you like most about the interview?</label>
            <textarea
              name="likedMost"
              value={formData.likedMost}
              onChange={handleTextChange}
              placeholder="Tell us what went well..."
              rows="3"
            />
          </div>
          
          <div className="form-group">
            <label>Any suggestions for improvement?</label>
            <textarea
              name="improvements"
              value={formData.improvements}
              onChange={handleTextChange}
              placeholder="How can we make the interview experience better?"
              rows="3"
            />
          </div>
          
          <div className="form-group">
            <label>Additional Comments</label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleTextChange}
              placeholder="Any other feedback you'd like to share..."
              rows="3"
            />
          </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InterviewFeedbackForm;